/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import clsx from "clsx"
import { observer } from "mobx-react-lite"
import type { CSSProperties } from "react"

import type { ApkDetail, ApkDetailThreatLevelName } from "../../features/apk-detail/apk-detail-types"
import { formatEnum } from "../../utils/format-enum"
import { valueOrDash } from "../../utils/value-or-dash"
import { getRiskClassName } from "../components/risk-score"
import { Badge } from "../primitives/badge"
import { Card, CardContent, CardHeader, CardTitle } from "../primitives/card"

import styles from "./apk-threat-level.module.css"

/**
 * Badge classes for each threat level.
 *
 * Coloured levels use solid backgrounds with white text for at least 4.5:1
 * contrast. PUA and `UNKNOWN` use neutral colours. Unlike the malware tab's
 * tinted badges, these solid fills distinguish threat levels on the detail page.
 */
const ThreatLevelColor = {
    MALWARE: "bg-destructive text-white",
    HIGHLY_DANGEROUS: "bg-orange-700 text-white",
    DANGEROUS: "bg-amber-700 text-white",
    POTENTIALLY_UNWANTED_APP: "bg-secondary text-secondary-foreground",
    SAFE: "bg-green-700 text-white",
    UNKNOWN: "bg-muted text-muted-foreground"
} as const satisfies Record<ApkDetailThreatLevelName, string>

interface Props {
    /**
     * APK threat level to display.
     *
     * The card uses the score defined by the threat level itself. The APK's
     * separate `score` from the AV SDK is unrelated and is not used here.
     */
    apk: Pick<ApkDetail, "threatLevel">
}

/**
 * Displays the APK's threat level and its associated score.
 *
 * Uses the same risk colour bands as the device risk score. An `UNKNOWN`
 * threat level is displayed as `N/A` with a grey meter. If the threat level
 * name is missing, the badge displays a dash.
 *
 * @example
 * <ApkThreatLevel apk={apk} />
 */
export const ApkThreatLevel = observer(({ apk }: Props) => {
    // UNKNOWN uses -1 as a sentinel for an unavailable score.
    const score = apk.threatLevel.name === "UNKNOWN" ? undefined : apk.threatLevel.score

    return (
        <Card>
            <CardHeader>
                <CardTitle>
                    <h3 className="m-0 text-xl font-bold uppercase">Threat Level</h3>
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <p
                        className={clsx(
                            "m-0 text-5xl font-semibold tabular-nums",
                            styles.score,
                            getRiskClassName(score)
                        )}
                    >
                        <span className="sr-only">Score: </span>
                        {score === undefined ? (
                            "N/A"
                        ) : (
                            <>
                                {score}
                                <span className="sr-only"> out of 100</span>
                            </>
                        )}
                    </p>
                    <Badge className={clsx("font-semibold", ThreatLevelColor[apk.threatLevel.name ?? "UNKNOWN"])}>
                        {valueOrDash(formatEnum(apk.threatLevel.name))}
                    </Badge>
                </div>
                <div className="grid gap-1" aria-hidden="true">
                    {/* Segmented score meter */}
                    <div className={styles.meter} style={{ "--score": score ?? 0 } as CSSProperties} />
                    <div className="flex justify-between text-xs text-muted-foreground tabular-nums">
                        <span>0</span>
                        <span>100</span>
                    </div>
                </div>
            </CardContent>
        </Card>
    )
})
